import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { FiPhone, FiMail, FiMapPin, FiArrowRight } from "react-icons/fi";
import SEO from "../common/SEO";
import HeaderOne from "../common/header/HeaderOne";
import FooterTwo from "../common/footer/FooterTwo";
import { useTranslation } from "react-i18next";

const AboutUs = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const principles = ["p1", "p2", "p3", "p4"];
  const audiences = ["s1", "s2", "s3"];

  const groupEntities = [
    { name: "Cyberate Technologies", desc: t("dddi.who_we_are.group_tech"), accent: false },
    { name: "Cyberate Project Management", desc: t("dddi.who_we_are.group_pm"), accent: false },
    { name: "Cyberate Investments", desc: t("dddi.who_we_are.group_inv"), accent: true },
  ];

  const OFFICE_ADDRESS = "5/15 Field St, Adelaide SA 5000, Australia";

  const mapUrl = useMemo(() => {
    const lang = { en: "en", zh: "zh-CN" };
    return `https://www.google.com/maps?q=${encodeURIComponent(OFFICE_ADDRESS)}&hl=${lang[i18n.language] || "en"}&output=embed`;
  }, [i18n.language]);

  const handleStartNavigation = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(OFFICE_ADDRESS)}`,
      "_blank"
    );
  };

  const contactItems = [
    {
      icon: <FiPhone size={28} />,
      textKey: "about_us.contact.phone",
      buttonKey: "about_us.contact.call_button",
      action: () => {
        window.location.href = "tel:+61871100111";
      },
    },
    {
      icon: <FiMail size={28} />,
      textKey: "about_us.contact.email",
      buttonKey: "about_us.contact.email_button",
      action: () => {
        window.location.href = "mailto:info@cyberate.investments";
      },
    },
    {
      icon: <FiMapPin size={28} />,
      textKey: "about_us.contact.location",
      buttonKey: "about_us.contact.consult_button",
      action: () => history.push("/FreeConsult"),
    },
  ];

  return (
    <>
      <SEO title="About Us - Cyberate Investments | A DDDI Group Company" />
      <main className="page-wrapper">
        <HeaderOne btnStyle="round" HeaderSTyle="header-not-transparent" />

        {/* Page head */}
        <section className="dddi-section" style={{ paddingBottom: 0 }}>
          <div className="container">
            <div className="dddi-section-head" style={{ marginBottom: 0 }}>
              <span className="dddi-eyebrow">{t("dddi.about.eyebrow")}</span>
              <h1 className="dddi-title">{t("dddi.about.title")}</h1>
              <p className="dddi-lede">{t("dddi.about.lede")}</p>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="dddi-section">
          <div className="container">
            <div className="dddi-grid dddi-grid--2">
              <div className="dddi-card">
                <h4 className="dddi-card-title">{t("about_us.mission.title")}</h4>
                <p className="dddi-card-text">{t("about_us.mission.content")}</p>
              </div>
              <div className="dddi-card">
                <h4 className="dddi-card-title">{t("about_us.vision.title")}</h4>
                <p className="dddi-card-text">{t("about_us.vision.content")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="dddi-section dddi-section--alt">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Since 2020</span>
              <h2 className="dddi-title">{t("dddi.about.story.title")}</h2>
            </div>
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <p className="dddi-lede" style={{ marginTop: 0 }}>
                {t("dddi.about.story.p1")}
              </p>
              <p className="dddi-lede">{t("dddi.about.story.p2")}</p>
              <p className="dddi-lede">{t("dddi.about.story.p3")}</p>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="dddi-section">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Principles</span>
              <h2 className="dddi-title">{t("dddi.about.principles.title")}</h2>
              <p className="dddi-lede">{t("dddi.about.principles.intro")}</p>
            </div>
            <div className="dddi-grid dddi-grid--4">
              {principles.map((key, index) => (
                <div key={key} className="dddi-step">
                  <div className="dddi-step-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <h4 className="dddi-card-title">
                    {t(`dddi.about.principles.${key}.title`)}
                  </h4>
                  <p className="dddi-card-text">
                    {t(`dddi.about.principles.${key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Work With */}
        <section className="dddi-section dddi-section--alt">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Clients</span>
              <h2 className="dddi-title">{t("dddi.about.serve.title")}</h2>
              <p className="dddi-lede">{t("dddi.about.serve.intro")}</p>
            </div>
            <div className="dddi-grid dddi-grid--3">
              {audiences.map((key) => (
                <div key={key} className="dddi-card">
                  <h4 className="dddi-card-title">
                    {t(`dddi.about.serve.${key}.title`)}
                  </h4>
                  <p className="dddi-card-text">
                    {t(`dddi.about.serve.${key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
            <p className="dddi-fineprint">{t("dddi.about.serve.note")}</p>
          </div>
        </section>

        {/* DDDI Group */}
        <section className="dddi-section">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Data · Development · Design · Insight</span>
              <h2 className="dddi-title">{t("dddi.about.group.title")}</h2>
              <p className="dddi-lede">{t("dddi.about.group.intro")}</p>
            </div>
            <div className="dddi-grid dddi-grid--3">
              {groupEntities.map((entity) => (
                <div
                  key={entity.name}
                  className={`dddi-card${entity.accent ? " dddi-card--accent" : ""}`}
                >
                  <h4 className="dddi-card-title">{entity.name}</h4>
                  <p className="dddi-card-text">{entity.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="dddi-section dddi-section--alt">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Contact</span>
              <h2 className="dddi-title">{t("about_us.contact.title")}</h2>
              <p className="dddi-lede">{t("about_us.contact.subtitle")}</p>
            </div>
            <div className="dddi-grid dddi-grid--3">
              {contactItems.map((c, idx) => (
                <div key={idx} className="dddi-card" style={{ textAlign: "center" }}>
                  <div style={{ color: "var(--dddi-gold)", marginBottom: 16 }}>
                    {c.icon}
                  </div>
                  <p className="dddi-card-text" style={{ marginBottom: 20 }}>
                    {t(c.textKey)}
                  </p>
                  <button className="dddi-btn" onClick={c.action}>
                    {t(c.buttonKey)}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office Location */}
        <section className="dddi-section">
          <div className="container">
            <div className="dddi-section-head">
              <span className="dddi-eyebrow">Adelaide, South Australia</span>
              <h2 className="dddi-title">{t("about_us.office_location.title")}</h2>
              <p className="dddi-lede">{t("about_us.office_location.subtitle")}</p>
            </div>
            <div className="dddi-panel" style={{ maxWidth: 960, margin: "0 auto" }}>
              <div style={{ height: 400, borderRadius: 8, overflow: "hidden" }}>
                <iframe
                  src={mapUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps - Cyberate Investments Office Location"
                ></iframe>
              </div>
              <div style={{ textAlign: "center", marginTop: 24 }}>
                <button className="dddi-btn" onClick={handleStartNavigation}>
                  {t("about_us.office_location.navigation_button")} <FiArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>

        <FooterTwo />
      </main>
    </>
  );
};

export default AboutUs;
